import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { VFC } from 'react';
import styled from 'styled-components';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

interface RetryIntervalsProps {
  editable: boolean;
  retryIntervals?: number[];
  updateRetryIntervals: (newIntervals: number[]) => void;
}

const IntervalRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacing[2]};
`;

const GrowInputField = styled.div`
  flex-grow: 1;
`;

const CenteredIcon = styled.div`
  cursor: pointer;
  margin-left: ${Spacing[2]};
`;

const MAXIMUM_NUMBER_OF_INTERVALS = 10;

/**
 * Renders a list of input fields that allows users to set retry intervals on a workflow action
 * @param {boolean} editable - Whether the retry intervals should be editable
 * @param {[number]} retryIntervals - Retry intervals to prefill the inputs with, defaults to empty
 * @param {(value: [number]) => void} updateRetryIntervals - A handler to update current retryIntervals
 */
const RetryIntervals: VFC<RetryIntervalsProps> = ({
  editable,
  retryIntervals = [],
  updateRetryIntervals,
}) => {
  const addNewRetry = () => {
    const newRetryIntervals =
      retryIntervals.length > 0 && retryIntervals.length < MAXIMUM_NUMBER_OF_INTERVALS
        ? retryIntervals.concat([retryIntervals[retryIntervals.length - 1] * 2])
        : [5];
    updateRetryIntervals(newRetryIntervals);
  };

  const editRetry = (indexToEdit: number, newNumber: string) => {
    updateRetryIntervals(Object.assign([], retryIntervals, { [indexToEdit]: Number(newNumber) }));
  };

  const deleteRetry = (indexToDelete: number) => {
    updateRetryIntervals([
      ...retryIntervals.slice(0, indexToDelete),
      ...retryIntervals.slice(indexToDelete + 1),
    ]);
  };

  return (
    <>
      <ParamLabel labelText='Retry Intervals' />
      <Paragraph aria-label='Retry description'>
        How many seconds to wait between retries when this step errors
      </Paragraph>
      {retryIntervals.map((interval, retryIndex) => (
        <IntervalRow key={retryIndex}>
          <GrowInputField>
            <Input
              type='number'
              key={retryIndex}
              value={interval}
              onChange={(e) => editRetry(retryIndex, e.target.value)}
              disabled={!editable}
              aria-label={`Edit retry ${retryIndex + 1}`}
              id={`retry-intervals__edit-interval-${retryIndex}`}
            />
          </GrowInputField>
          {editable && (
            <CenteredIcon
              onClick={() => deleteRetry(retryIndex)}
              role='button'
              tabIndex={0}
              aria-label={`Delete retry ${retryIndex + 1}`}
              id={`retry-intervals__delete-interval-${retryIndex}`}
            >
              <XIcon />
            </CenteredIcon>
          )}
        </IntervalRow>
      ))}
      {retryIntervals.length < MAXIMUM_NUMBER_OF_INTERVALS && (
        <>
          <Spacer height={2} />
          <div>
            <Button
              variant='basic'
              width='auto'
              disabled={!editable}
              onClick={addNewRetry}
              id='retry-intervals__add-interval'
              aria-label='Add Retry'
            >
              {retryIntervals.length > 0 ? 'Add Retry' : 'Configure'}
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default RetryIntervals;
